import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import hero1 from "../../../../public/assets/images/supportimage.jpeg";
import hero2 from "../../../../public/assets/images/speedtest.jpg";
import hero3 from "../../../../public/assets/images/fiber-5.jpg";

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      image: hero1,
      badge: { icon: "Zap", text: "Future-Ready Infrastructure" },
      title: "Transform Your Business with",
      highlightText: "Future-Ready Fiber",
      subtitle: "Infrastructure",
      description:
        "Seamlessly transition from outdated copper systems to enterprise-grade fiber optic networks. Experience minimal disruption with maximum performance gains.",
      keyPoints: [
        { icon: "Cable", text: "Seamless Copper-to-Fiber Conversion" },
        { icon: "Shield", text: "Minimal Business Disruption" },
        { icon: "Award", text: "Enterprise-Grade Reliability" },
      ],
      trustMetrics: [
        { value: "99.9%", label: "Project Success Rate" },
        { value: "10x", label: "Average Speed Improvement" },
        { value: "24/7", label: "Emergency Support" },
      ],
    },
    {
      id: 2,
      image: hero2,
      badge: { icon: "Building2", text: "Enterprise Solutions" },
      title: "Scale Your Network",
      highlightText: "Infrastructure",
      subtitle: "for Growth",
      description:
        "Future-proof your business with scalable fiber solutions designed to grow with your needs. Support unlimited bandwidth demands and emerging technologies.",
      keyPoints: [
        { icon: "TrendingUp", text: "Scalable Bandwidth Solutions" },
        { icon: "Server", text: "Enterprise-Grade Equipment" },
        { icon: "Globe", text: "Multi-Location Connectivity" },
      ],
      trustMetrics: [
        { value: "500+", label: "Enterprises Served" },
        { value: "5 Years", label: "Average ROI Timeline" },
        { value: "100%", label: "Scalability Guarantee" },
      ],
    },
    {
      id: 3,
      image: hero3,
      badge: { icon: "ShieldCheck", text: "24/7 Expert Support" },
      title: "Professional",
      highlightText: "Installation",
      subtitle: "& Reliable Support Services",
      description:
        "Certified technicians provide professional installation with ongoing support. Minimize downtime with our proven deployment methodology and emergency response.",
      keyPoints: [
        { icon: "UserCheck", text: "Certified Installation Teams" },
        { icon: "Clock", text: "Scheduled Deployment Windows" },
        { icon: "Phone", text: "24/7 Emergency Response" },
      ],
      trustMetrics: [
        { value: "2 Hours", label: "Average Response Time" },
        { value: "15+ Years", label: "Industry Experience" },
        { value: "98%", label: "First-Time Fix Rate" },
      ],
    },
  ];

  // Auto-rotate slides
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const currentSlideData = slides[currentSlide];

  return (
    <section className="relative w-full h-[900px] overflow-hidden">
      {/* Background Images */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
            index === currentSlide ? "opacity-100 z-0" : "opacity-0"
          }`}
          style={{ backgroundImage: `url(${slide.image})` }}
        >
          <div className="absolute inset-0 bg-black/15" />
          </div>
      ))}

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 z-10"></div>

      {/* Content */}
      <div className="relative z-20 flex items-center justify-center h-full px-10">
        <div className="max-w-4xl text-center space-y-8 text-white">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full border shadow-md">
            <Icon
              name={currentSlideData?.badge?.icon}
              size={16}
              className="text-emerald-300"
            />
            <span className="text-sm font-medium">
              {currentSlideData?.badge?.text}
            </span>
          </div>

          {/* Heading */}
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            {currentSlideData?.title}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-300">
              {currentSlideData?.highlightText}
            </span>{" "}
            {currentSlideData?.subtitle}
          </h1>

          {/* Description */}
          <p className="text-lg text-gray-200 max-w-2xl mx-auto">
            {currentSlideData?.description}
          </p>

          {/* Trust Metrics */}
          <div className="flex items-center justify-center space-x-6 pt-4">
            {currentSlideData?.trustMetrics?.map((metric, i) => (
              <React.Fragment key={i}>
                <div className="text-center">
                  <div className="text-2xl font-bold">{metric?.value}</div>
                  <div className="text-sm">{metric?.label}</div>
                </div>
                {i < currentSlideData?.trustMetrics?.length - 1 && (
                  <div className="w-px h-12 bg-white/50"></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      {/* Scroll Down Arrow */}
      <button
  onClick={() =>
    window.scrollTo({
      top: window.innerHeight,
      behavior: "smooth",
    })
  }
  className="hidden sm:flex absolute bottom-10 right-10 z-30 p-5 rounded-full 
             bg-gradient-to-r from-blue-500 to-emerald-400 
             hover:from-blue-600 hover:to-emerald-500
             shadow-xl shadow-emerald-500/40
             hover:scale-110 transition-all duration-300 ease-out
             animate-bounceOnce"
>
  <Icon name="ArrowDown" size={48} className="text-white" />
</button>


      {/* Dots */}
      {/* <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 z-30">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentSlide(i)}
            className={`w-4 h-4 rounded-full border-2 transition ${
              i === currentSlide ? "bg-white border-white" : "border-gray-300"
            }`}
          />
        ))}
      </div> */}
    </section>
  );
};

export default HeroSection;
